import { useEffect, useLayoutEffect, useRef } from 'react'
import { useLocation, useNavigationType } from 'react-router'

/**
 * Scroll management for route transitions — the browser's own contract:
 *
 *  - A NEW navigation (link click, footer, header logo, a redirect that
 *    REPLACEs the entry — /billing → /dashboard) lands at the TOP of the page.
 *    Without this, scrolling down to the footer and clicking
 *    "CLI" mounted the new route already scrolled — the browser keeps the
 *    viewport unless something resets it.
 *  - Back/forward (POP) RESTORES where you were on that route — both the
 *    immediate back and a later return in the same tab (positions are keyed
 *    by path in sessionStorage, so returning to the route later in the same
 *    tab has its position on file; a PUSH — or a REPLACE onto another path —
 *    deliberately starts fresh at the top, "first time on the route", while
 *    POP is the only restore path, matching how browsers and the back/forward
 *    buttons behave).
 *
 * Positions are saved continuously while a route is open (scroll listener +
 * effect cleanup), under the path they belong to — the cleanup captures the
 * PREVIOUS path from a ref, because by cleanup time the router has already
 * swapped location. The ledger is capped ({@link MAX_SCROLL_LEDGER_ENTRIES},
 * oldest writes dropped): one entry per path ever visited is an unbounded
 * map, and every save round-trips the whole ledger through JSON.
 *
 * Both of those contracts are about a NAVIGATION, and a URL hash can mean two
 * different things in this app — a section ANCHOR, whose landing owns the
 * viewport, or the catalog's SEARCH (`#q=…`), which is app state no element
 * answers to. Only the anchor changes what a navigation owes the viewport:
 * see {@link isAnchorHash}.
 *
 * The IDE/workspace is excluded — it manages its own viewport (panels, file
 * tree) and a forced scroll would fight it.
 */
const KEY = 'mol.scroll'

/**
 * The ledger holds one position per path VISITED this tab-session, and every
 * throttled save parses + serializes the whole thing — without a cap both the
 * storage and the per-save work grow with the session's entire route history,
 * forever. A hundred paths is far past any real back/forward reach (a user
 * returns to the last handful of routes), so the oldest writes are dropped.
 */
export const MAX_SCROLL_LEDGER_ENTRIES = 100

/**
 * How long the POP restore keeps re-landing a position its incoming route
 * cannot hold yet (ms).
 *
 * A route whose content arrives AFTER mount — a lazy chunk, then a data fetch:
 * the analytics/fleet surface, the dashboard — is shorter than the saved
 * position for its first frames, and the browser CLAMPS a write its document
 * cannot accept. This is the budget the landing stays armed for while the
 * document grows; past it the restore gives up rather than keep forcing a
 * layout per frame. The same window the anchor landing polls for.
 */
export const RESTORE_SETTLE_MS = 2500

const read = (): Record<string, number> => {
  try {
    // Only a plain object is a ledger. JSON.parse SUCCEEDS on the scalar junk
    // storage can end up holding (`null` from `JSON.stringify(null)` written
    // by a devtools session, an extension, or an older shape) — and the POP
    // restore below indexes the result unguarded, where a `null` "ledger"
    // threw on every Back/Forward and took each navigation into the error
    // boundary until storage was cleared. Any other shape reads as "no
    // positions", which is what a corrupt ledger means. An ARRAY is
    // `typeof 'object'` too and would slip through the object check — but
    // JSON.stringify serializes an array by INDEX keys only, so every saved
    // path would be silently dropped on the next write: the corrupt ledger
    // would re-write itself and never heal. Reject it like the scalars.
    const parsed: unknown = JSON.parse(sessionStorage.getItem(KEY) ?? '')
    if (parsed === null || Array.isArray(parsed) || typeof parsed !== 'object') return {}
    // The VALUES are positions — the type this function returns promises a
    // number per path, and both readers depend on it: the POP restore hands
    // the value to `window.scrollTo`, and the throttled save round-trips the
    // whole ledger through JSON on every scroll tick. Read raw, one hostile
    // or corrupt entry (an extension, a devtools session, a dependency gone
    // rogue writing same-origin storage) SURVIVED every shape guard and was
    // then re-serialized verbatim on every save — a multi-kilobyte nested
    // object under a path key turned each 100 ms save into a parse+stringify
    // of that object, forever, and a junk value reached scrollTo non-numeric.
    // A value that is not a finite number is not a position: drop it, so the
    // next write stores only positions and the ledger heals.
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).filter(
        (entry): entry is [string, number] =>
          typeof entry[1] === 'number' && Number.isFinite(entry[1]),
      ),
    )
  } catch (_error) {
    // Absent/corrupt ledger — no positions to restore; not an error path.
    return {}
  }
}

const store = (path: string, y: number): void => {
  try {
    const all = read()
    // Delete-then-set so the written path reads as the MOST RECENT entry
    // (assigning an existing key keeps its original insertion slot), then
    // drop the oldest writes past the cap — the route being read always
    // survives its own save.
    delete all[path]
    all[path] = Math.round(y)
    const paths = Object.keys(all)
    for (let i = 0; i < paths.length - MAX_SCROLL_LEDGER_ENTRIES; i++) {
      delete all[paths[i]]
    }
    sessionStorage.setItem(KEY, JSON.stringify(all))
  } catch (_error) {
    // Storage full/blocked — restoration is a nicety, never a failure.
  }
}

/**
 * Routes that manage their own scroll — the IDE shell ALONE. `/workspace/*`
 * owns its viewport (panels, file tree), so a forced scroll would fight it.
 * The dashboard is NOT one of them: it is an ordinary scrolling page with no
 * scroll handling of its own, and exempting it silently dropped both
 * behaviours this module documents for `/dashboard` — the landing's sticky
 * header links there, so clicking "Dashboard" from far down the landing
 * mounted the dashboard at the landing's y, and the dashboard's own position
 * was never saved or restored.
 */
const EXCLUDED = /^\/workspace/

/**
 * decodeURIComponent throws URIError on a malformed `%` sequence (`#%E0` — a
 * truncated multi-byte escape, one wrong copy-paste in a shared link), and a
 * throw in the effect body takes the whole app into the error boundary. The
 * hash is also NOT a percent-encoded value the app authored: the anchor ids
 * it targets are raw text, so a sequence that cannot decode degrades to the
 * raw text (the browser's own fragment navigation never decodes either).
 */
const safeDecodeHash = (raw: string): string => {
  try {
    return decodeURIComponent(raw)
  } catch (_error) {
    return raw
  }
}

/**
 * The hash prefixes that are APP STATE, never a section anchor — no element
 * carries an id spelled like one. The list is the app's own inventory of the
 * hashes it writes and reads:
 *
 *  - `#q=…` — the catalog's search: written by client-search-react's
 *    `hashForQuery` and read back by `readHashQuery` (CatalogLayout does, with
 *    the same `q=` convention);
 *  - `#prompt=…` — the landing's shareable prompt link: the form `promptLink`
 *    writes and `promptFromLocation` reads back into the hero prompt box
 *    (`lib/prompt-link.ts`, Landing's prefill effect).
 *
 * A NEW hash form the app grows belongs here in the same commit, or the anchor
 * branches below take it — the failure each entry was added for.
 */
const NON_ANCHOR_HASH_PREFIXES = ['#q=', '#prompt='] as const

/**
 * The app puts THREE things in the URL hash, and only ONE of them is an anchor:
 *
 *  - a section anchor (`#bonds-swap`) — an element carries that id, so its
 *    landing owns the viewport;
 *  - the catalog's search (`#q=…`) — app state that NO element answers to;
 *  - the landing's shared-prompt link (`#prompt=…`) — app state too, and the
 *    page it lands on is the landing's own TOP, where the box it prefills
 *    sits ({@link NON_ANCHOR_HASH_PREFIXES} lists both).
 *
 * Only an anchor hash may take the anchor branches below. Treated as an anchor,
 * a non-anchor hash has no landing to protect, so it (a) stood the POP restore
 * down — Back out of an open template left the grid at the top instead of where
 * the reader was, against the documented POP contract — and (b) skipped the
 * documented top landing of a NEW navigation, so a phone typing into the search
 * box over an open document landed mid-list (`#q=`), and a `#prompt=` push left
 * the visitor mid-page with the prefilled prompt box off screen — while the
 * anchor branch polled for an element named `prompt=…` that cannot exist.
 *
 * A bare hash (`#auth`) stays an anchor here even though CatalogLayout can read
 * one as a legacy filter: both forms the app writes carry their own marker
 * ({@link NON_ANCHOR_HASH_PREFIXES}), and an anchor is the safe reading for an
 * id-shaped hash it did not write.
 *
 * @param hash - The hash from the location, with its `#`.
 * @returns True when the hash is a section anchor.
 */
const isAnchorHash = (hash: string): boolean =>
  hash !== '' && !NON_ANCHOR_HASH_PREFIXES.some((prefix) => hash.startsWith(prefix))

export function ScrollManager(): null {
  const location = useLocation()
  const navigationType = useNavigationType() // 'PUSH' | 'POP' | 'REPLACE'
  // The path the CURRENT effect body was mounted for — cleanup uses it to
  // save the final scroll under the route it actually measured.
  const mountedPath = useRef(location.pathname)
  // The path the ROUTER holds right now, written in the COMMIT phase (a layout
  // effect, never during render — a render React throws away would otherwise
  // leave the ref on a route that never reached the screen). The scroll
  // listener's own guard needs it: `mountedPath` cannot answer "is my route
  // still the one on screen?" — that ref is written by the same effect run
  // whose `location` the listener closes over, so comparing the two is a
  // tautology that never trips. What it must catch is the browser's CLAMP
  // scroll for the incoming page: the router pushes the new URL, React renders
  // and swaps the new (lazy, near-empty) DOM in, and the browser lays the
  // shrunk document out and fires that scroll — all before the outgoing
  // listener is removed with the PASSIVE cleanup. Left unguarded, the clamp
  // wrote the incoming page's 0 into `lastY` and the cleanup saved it under
  // the route just left, so Back landed at the top.
  const livePath = useRef(location.pathname)
  useLayoutEffect(() => {
    livePath.current = location.pathname
  })

  useEffect(() => {
    const path = location.pathname
    // The path the PREVIOUS effect run served — the route being left. The
    // router has already swapped `location` by the time this body runs, so
    // `location.pathname` alone cannot answer "is this a different page?" — a
    // REPLACE onto a NEW route (the /billing → /dashboard redirect, the
    // post-login replace) reads the same as a REPLACE that only re-writes the
    // query string under the page the user is already reading; only the first
    // owes the viewport a landing (see the branches below).
    const previousPath = mountedPath.current
    const pathChanged = previousPath !== path
    mountedPath.current = path
    // Every rAF chain armed below outlives the tick that spawned it, and the
    // router does not clean them up: without a kill switch tied to THIS effect
    // run, a chain survives the next navigation. The arrival top-hold kept
    // forcing y=0 for the rest of its 350 ms budget while a quick Back's
    // position restore wrote the saved y under it — the restore lost, and the
    // page ended pinned at the top. `cancelled` dies in the cleanup, so each
    // chain's lifetime is exactly the navigation that armed it (its own
    // hash/budget guards stay: they stop a chain early WITHIN its navigation).
    let cancelled = false
    if (EXCLUDED.test(path)) return

    if (navigationType === 'POP') {
      // A same-page anchor click (the On-this-page rail, in-page links)
      // arrives as a POP — the router walks history for hash-only changes.
      // Its contract is the ANCHOR: the browser's fragment scroll and the
      // layout's hashchange landing handle it, and a position restore here
      // stomps that landing (2026-10-04: the rail's first click restored the
      // saved y=0 over it, so the page didn't move until the second click).
      // Only the RESTORE is skipped — the scroll tracking below still arms:
      // this used to `return` out of the whole effect, dropping the listener
      // and the cleanup, so a route arrived at with a hash (a rail click, a
      // Back onto a #link) stopped saving positions for the rest of the visit
      // and Back later restored a pre-anchor position (or none).
      //
      // And only for an ANCHOR hash — see {@link isAnchorHash}: the catalog's
      // `#q=` search rides every catalog URL a search has touched, including
      // the entry a Back returns to, and no element answers to it, so standing
      // the restore down there silently retired the documented POP contract
      // (Back out of an open template left the grid at the top instead of
      // where the reader was).
      if (!isAnchorHash(location.hash)) {
        const y = read()[path]
        if (y !== undefined) {
          // Land the position, and KEEP landing it while the incoming document
          // cannot accept it yet. A write to a document shorter than `y` is
          // CLAMPED by the browser to that document's current maximum — and
          // every route whose content arrives AFTER mount (the lazy chunk, then
          // the data fetch: the analytics/fleet surface, the dashboard) is in
          // exactly that state for its first frames. Two frames is not enough
          // for a fetch, so the restore used to clamp at the top and STAY there:
          // the clamp was the end of the chain and nothing re-armed it when the
          // page grew, so Back out of a scrolled analytics page landed at the
          // top for good. The landing now stays armed as the document grows,
          // bounded by WALL CLOCK (not frames — rAF stalls in a background tab,
          // where a frame-count budget would park the chain and resume minutes
          // later). `cancelled` still ends it with its navigation, and the
          // first two writes are kept: a page that lays out tall and then
          // shrinks must not keep the position either.
          const startedAt = performance.now()
          let writes = 0
          const restore = (): void => {
            if (cancelled) return
            window.scrollTo(0, y)
            writes += 1
            // The document can hold the position when its scrollable height
            // reaches `y` (a non-positive `y` is the top, nothing to land).
            const canHold =
              y <= 0 || document.documentElement.scrollHeight - window.innerHeight >= y
            if ((writes >= 2 && canHold) || performance.now() - startedAt >= RESTORE_SETTLE_MS)
              return
            requestAnimationFrame(restore)
          }
          requestAnimationFrame(restore)
        }
      }
    } else if (isAnchorHash(location.hash)) {
      // A cross-page #anchor link (docs search results, tag links) or a
      // redirect onto one: the contract is the ANCHOR, not the top. The
      // target route's lazy chunk may still be mounting, so poll for the
      // element and land on it once it exists. No top-hold — it would fight
      // this landing and win. Bounded by WALL CLOCK, not frames: rAF stalls in
      // a background tab, and a frame-count budget parks the poll there,
      // resuming minutes later to re-scroll a page the user has been reading.
      //
      // Not gated on PUSH vs REPLACE (only POP, above, is excluded): both
      // bring the router to an anchored route, and both owe it that anchor.
      // The hash alone decides here.
      //
      // Only an ANCHOR hash may take this branch ({@link isAnchorHash}): a
      // `#q=` search hash answers to no element, so the poll chased nothing
      // for its whole budget AND the arrival skipped the top landing below —
      // a NEW navigation's documented contract — leaving the incoming page
      // exactly where the browser clamped the previous one. A `#q=` push now
      // falls through to the top landing like every other new navigation.
      const startedAt = performance.now()
      const target = safeDecodeHash(location.hash.slice(1))
      const land = (): void => {
        // Dead with its navigation (the cleanup) — and dead the moment the
        // hash moves within it: the new anchor's own fragment scroll and the
        // layout's hashchange landing own the viewport now, and a chain still
        // running here would yank the page back to THIS anchor every frame
        // for the rest of its budget (the first click on the On-this-page
        // rail landed nowhere because of exactly this class of fight).
        if (cancelled || window.location.hash !== location.hash) return
        // The poll exists to wait out the lazy chunk: once the element is
        // mounted and landed, the chain is DONE — re-issuing scrollIntoView
        // every frame until the budget expires would pin the viewport to the
        // anchor against every scroll the user makes, and force a layout per
        // frame the whole time.
        const el = document.getElementById(target)
        if (el) {
          el.scrollIntoView()
          return
        }
        // The SAME budget the POP restore keeps above — one constant, so the
        // two landing paths cannot drift apart (this read a literal 2500 the
        // doc's "same window" promise did not tie to the constant).
        if (performance.now() - startedAt < RESTORE_SETTLE_MS) requestAnimationFrame(land)
      }
      requestAnimationFrame(land)
    } else if (navigationType === 'PUSH' || pathChanged) {
      window.scrollTo(0, 0)
      // The browser may fire its own scroll (layout shift of the incoming
      // page) AFTER our scrollTo — hold the top briefly until the new route
      // settles, then release control to the user. Bounded by WALL CLOCK and
      // by the hash, never by frame count: a frame-count budget parks the
      // rAF chain the moment the tab stops painting (background tab) and it
      // RESUMES MID-INTERACTION later, force-scrolling the page back to the
      // top over the user's first anchor click (2026-10-04: the docs On-
      // this-page rail's first click scrolled nowhere because of exactly
      // this — the arrival hold woke on the click's frames and pinned y=0).
      //
      // `pathChanged` brings a REPLACE onto a new route here — /billing
      // redirects to /dashboard, login/signup replace into the app: a route
      // swap the user did not ask for is still a new page, and without this it
      // matched NO branch at all, so the dashboard mounted at whatever offset
      // the previous page was left at (the browser keeps the viewport across
      // an SPA route swap). A same-path REPLACE — the query-string sync, which
      // this app does not do yet — still lands nothing: it re-writes one page's
      // URL, it does not open another.
      const startedAt = performance.now()
      const hashAtArrival = location.hash
      const hold = (): void => {
        if (cancelled) return
        const elapsed = performance.now() - startedAt
        if (elapsed > 350 || window.location.hash !== hashAtArrival) return
        if (window.scrollY !== 0) window.scrollTo(0, 0)
        requestAnimationFrame(hold)
      }
      requestAnimationFrame(hold)
    }

    // Scroll events fire per frame, and each store() round-trips the WHOLE
    // ledger through JSON (parse + stringify) and sessionStorage — work that
    // scales with every path visited this session, paid on every frame of
    // every scroll. The exact final position never depends on this listener:
    // the cleanup below writes it when the route is left. So save at the same
    // 100 ms cadence as the docs scroll-spy (timestamp-guarded, not rAF — rAF
    // stalls in a backgrounded tab).
    // Track the position this route was actually left at. The cleanup below
    // writes the FINAL position, and it runs AFTER the router has swapped the
    // next route's DOM in: every route here is lazy (`Suspense fallback={null}`),
    // so for a frame the incoming document is nearly empty and the browser
    // clamps scrollY to its new, tiny maximum at layout — a live
    // `window.scrollY` read at cleanup time was the INCOMING route's clamped
    // position, saved under the route just left, and the Back restore landed
    // at the top. `lastY` is updated by every scroll event before the
    // throttled store, so it is the freshest position the route itself
    // reached (sub-100 ms precise where the throttle skips a write).
    let lastY = window.scrollY
    let lastSave = 0
    const onSave = () => {
      // Only remember while the listener's own route is the one on screen —
      // see `livePath` above: the ref moves with the router's render, so the
      // incoming page's clamp scroll (fired before this listener is removed)
      // no longer counts as this route's own last position.
      if (livePath.current !== location.pathname) return
      lastY = window.scrollY
      const now = Date.now()
      if (now - lastSave < 100) return
      lastSave = now
      store(path, lastY)
    }
    window.addEventListener('scroll', onSave, { passive: true })
    return () => {
      cancelled = true
      window.removeEventListener('scroll', onSave)
      // Save the FINAL position under the route this effect served (the ref),
      // from the tracked value — never a live scrollY read, which by cleanup
      // time belongs to the route being entered (see lastY above).
      store(mountedPath.current, lastY)
    }
    // `location.hash` is in the deps so a navigation that changes ONLY the
    // hash re-arms this effect: the docs search navigates with the router
    // (navigate('page#section')), and when the best hit is a section of the
    // page the user is ALREADY on, pathname and navigation type are unchanged
    // — without the hash in the deps nothing re-ran, and a router pushState
    // fires no hashchange either (that is a native-navigation event), so the
    // URL moved while the viewport stayed put. The layout's hashchange landing
    // keeps the NATIVE anchor clicks; this effect owns the router-driven ones.
  }, [location.pathname, location.hash, navigationType])

  return null
}
